"use client";

import { getPersonalizeText } from "@/lib/personalize-store";
import { useEffect, useMemo, useState } from "react";
import { Cta } from "@/components/marketing/Cta";
import { PRINT_FINISHES, type PrintFinish } from "@/lib/print-catalog";
import { CheckoutDisclosure } from "@/components/legal/CheckoutDisclosure";

/* Physical wall-art print purchase panel (Rina ask, 2026-06-14). Sits
 * under the digital buy block on /digital-art/[slug]. The buyer picks a
 * finish (framed / canvas / poster) + size; we POST a `print` item to
 * /api/shop/checkout, which prices it server-side, collects the shipping
 * address in Stripe, and — on payment — places the Printful order that
 * prints, frames, and ships it. No digital file involved; this is the
 * real thing on their wall.
 *
 * Theme: dark, matches WallArtHeroBuy. */

export function PhysicalPrintPanel({
  slug,
  title,
}: {
  slug: string;
  title: string;
}) {
  const [finishKey, setFinishKey] = useState<PrintFinish>("framed");
  const [sizeKey, setSizeKey] = useState<string>("16x20");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Print credit: a code from the digital purchase email (or ?credit= in
   * the link it carries). Checked read-only here so the button can show
   * the money off; the checkout re-checks it server-side. */
  const [credit, setCredit] = useState("");
  const [creditInfo, setCreditInfo] = useState<{ cents: number; label: string } | null>(null);
  const [creditError, setCreditError] = useState<string | null>(null);
  const [showCredit, setShowCredit] = useState(false);

  useEffect(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("credit");
      if (fromUrl) {
        setCredit(fromUrl.toUpperCase());
        setShowCredit(true);
      }
    } catch {
      /* no URL access — nothing to prefill */
    }
  }, []);

  useEffect(() => {
    const code = credit.trim();
    setCreditInfo(null);
    setCreditError(null);
    if (code.length < 8) return;
    let alive = true;
    const t = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(`/api/print/credit?code=${encodeURIComponent(code)}&slug=${encodeURIComponent(slug)}`);
          const j = (await res.json()) as { ok?: boolean; cents?: number; label?: string; error?: string };
          if (!alive) return;
          if (j.ok && typeof j.cents === "number") setCreditInfo({ cents: j.cents, label: j.label ?? "" });
          else setCreditError(j.error ?? "That code is not live.");
        } catch {
          if (alive) setCreditError("Could not check that code.");
        }
      })();
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [credit, slug]);

  const finish = useMemo(
    () => PRINT_FINISHES.find((f) => f.finish === finishKey) ?? PRINT_FINISHES[0]!,
    [finishKey],
  );
  const variant = useMemo(
    () => finish.variants.find((v) => v.size === sizeKey) ?? finish.variants[1] ?? finish.variants[0]!,
    [finish, sizeKey],
  );

  const onOrder = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/shop/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              kind: "print",
              slug,
              finish: finish.finish,
              size: variant.size,
              quantity: 1,
              text: getPersonalizeText() || undefined,
            },
          ],
          email: email.trim() || undefined,
          credit: creditInfo ? credit.trim() : undefined,
          successPath: `/thanks?slug=${slug}&print=1&session={CHECKOUT_SESSION_ID}`,
          cancelPath: `/digital-art/${slug}`,
        }),
      });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Could not start checkout.");
      }
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout unavailable.");
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border p-6 md:p-8"
      style={{
        borderColor: "var(--color-border-default)",
        background:
          "linear-gradient(180deg, rgba(217,163,90,0.06), transparent 60%)",
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span
          className="font-mono text-[10px] uppercase"
          style={{ color: "var(--color-accent)", letterSpacing: "0.16em" }}
        >
          ↳ Want it on your wall? · Printed &amp; shipped
        </span>
        <span className="font-mono text-[10px] uppercase text-white/65" style={{ letterSpacing: "0.14em" }}>
          No printer needed
        </span>
      </div>
      <h3
        className="mt-3 text-white"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "clamp(22px, 3vw, 32px)",
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
        }}
      >
        Order it as a real framed print.
      </h3>
      <p className="mt-2 max-w-[58ch] text-[14px] leading-[1.55] text-white/65">
        Our print partner prints, frames, and ships a physical {title} straight
        to your door — you never touch a printer or a frame shop. Billed at checkout.
      </p>

      {/* Finish selector */}
      <div className="mt-6">
        <span className="mb-2 block font-mono text-[10px] uppercase text-white/65" style={{ letterSpacing: "0.16em" }}>
          Finish
        </span>
        <div className="flex flex-wrap gap-2">
          {PRINT_FINISHES.map((f) => {
            const selected = f.finish === finishKey;
            const from = Math.min(...f.variants.map((v) => v.priceCents));
            return (
              <button
                key={f.finish}
                type="button"
                data-interactive
                onClick={() => {
                  setFinishKey(f.finish);
                  if (!f.variants.some((v) => v.size === sizeKey)) {
                    setSizeKey(f.variants[1]?.size ?? f.variants[0]!.size);
                  }
                }}
                className="rounded-lg border px-3.5 py-2.5 text-left transition-colors"
                style={{
                  borderColor: selected ? "rgba(217,163,90,0.55)" : "var(--color-border-default)",
                  background: selected ? "rgba(217,163,90,0.12)" : "rgba(255,255,255,0.02)",
                }}
              >
                <div
                  className="text-[13px] font-medium"
                  style={{ color: selected ? "var(--color-accent)" : "#fff" }}
                >
                  {f.label}
                </div>
                <div className="mt-0.5 font-mono text-[10px] uppercase text-white/65" style={{ letterSpacing: "0.1em" }}>
                  from ${(from / 100).toFixed(0)}
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[12.5px] leading-[1.5] text-white/65">{finish.blurb}</p>
      </div>

      {/* Size selector */}
      <div className="mt-5">
        <span className="mb-2 block font-mono text-[10px] uppercase text-white/65" style={{ letterSpacing: "0.16em" }}>
          Size
        </span>
        <div className="flex flex-wrap gap-2">
          {finish.variants.map((v) => {
            const selected = v.size === variant.size;
            return (
              <button
                key={v.size}
                type="button"
                data-interactive
                onClick={() => setSizeKey(v.size)}
                className="rounded-lg border px-3.5 py-2 transition-colors"
                style={{
                  borderColor: selected ? "rgba(217,163,90,0.55)" : "var(--color-border-default)",
                  background: selected ? "rgba(217,163,90,0.12)" : "rgba(255,255,255,0.02)",
                }}
              >
                <span className="text-[13px]" style={{ color: selected ? "var(--color-accent)" : "#fff" }}>
                  {v.label}
                </span>
                <span className="ml-2 font-mono text-[11px] text-white/65">${(v.priceCents / 100).toFixed(0)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Email + order */}
      <div className="mt-7 space-y-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email for your order confirmation"
          autoComplete="email"
          className="w-full rounded-lg border bg-black/40 px-4 py-3 text-[15px] outline-none focus:border-[#1E64FF]/40"
          style={{ borderColor: "rgba(255,255,255,0.08)", color: "var(--color-fg)" }}
        />
        {/* Print credit — from the digital purchase email. */}
        <div className="mb-3">
          {showCredit ? (
            <>
              <label htmlFor="print-credit" className="mb-2 block font-mono text-[10px] uppercase text-white/65" style={{ letterSpacing: "0.16em" }}>
                Print credit code
              </label>
              <input
                id="print-credit"
                type="text"
                value={credit}
                onChange={(e) => setCredit(e.target.value.toUpperCase())}
                placeholder="APEX-PRINT-XXXXXX"
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full rounded-lg border bg-black/40 px-4 py-3 font-mono text-[14px] tracking-[0.08em] outline-none focus:border-[#1E64FF]/40"
                style={{ borderColor: creditInfo ? "rgba(217,163,90,0.6)" : "rgba(255,255,255,0.08)", color: "var(--color-fg)" }}
              />
              {creditInfo && (
                <p className="mt-2 text-[13px]" style={{ color: "var(--color-accent)" }}>
                  {creditInfo.label} — what you paid for the digital file, applied at checkout.
                </p>
              )}
              {creditError && (
                <p className="mt-2 text-[13px]" style={{ color: "#ef4444" }}>
                  {creditError}
                </p>
              )}
            </>
          ) : (
            <button type="button" onClick={() => setShowCredit(true)} className="font-mono text-[11px] uppercase tracking-[0.14em] underline underline-offset-4 text-white/65">
              Bought the digital file? Use your print credit
            </button>
          )}
        </div>
        <Cta variant="primary" onClick={onOrder} disabled={busy} full>
          {busy
            ? "Opening checkout…"
            : creditInfo
              ? `Order ${finish.label.toLowerCase()} — $${(Math.max(0, variant.priceCents - creditInfo.cents) / 100).toFixed(0)} after your $${(Math.min(creditInfo.cents, variant.priceCents) / 100).toFixed(0)} credit`
              : `Order ${finish.label.toLowerCase()} — $${(variant.priceCents / 100).toFixed(0)}`}
        </Cta>
        {error && (
          <p className="text-[13px]" style={{ color: "#ef4444" }}>
            {error}
          </p>
        )}
        <p className="font-mono text-[10px] uppercase text-white/65" style={{ letterSpacing: "0.12em" }}>
          ↳ Shipping address collected at checkout · 2–5 day production · tracked delivery
        </p>
      </div>
      <CheckoutDisclosure physical className="mt-3" />
    </div>
  );
}
